/**
 * kult-home-video.js — Röst-Video der Startseite (erste Section), das einmal von selbst abläuft
 * (sections/kult-home-video.liquid). Das Video ist immer stumm (Datei ohne Tonspur; muted/playsinline/preload auto
 * als Attribut im HTML und hier zusätzlich als Property).
 * Start: IntersectionObserver; sobald die Section zu ≥ 50 % sichtbar ist (bei sehr hoher Section: ≥ halber Viewport),
 * video.play(). Das Video läuft genau einmal: verlässt die Section den Viewport ganz, pausiert es und läuft beim
 * nächsten Sichtkontakt an derselben Stelle weiter. Nach „ended“ bleibt das letzte Bild mit Szene E stehen
 * (kult-video--ended); der Textlink [data-video-replay] („Nochmal ansehen“) startet von vorn.
 * Takt: rAF-Schleife während des Abspielens (Start bei play/playing, Ende bei pause/ended), timeupdate als
 * Sicherheitsnetz. Aus video.currentTime: data-scene (a; ab t_b b; ab t_c c; ab t_d d; ab t_e e), --p (currentTime/
 * Dauer, 4 Stellen), --b (Röstfortschritt t_b → t_d; Readout-Balken, Crack-Marke, Kult-Punkt), der Röstzeit-Timer
 * (linear 0:00 bis roast_minutes:00 zwischen t_b und t_d, danach Endwert; m:ss + Einheit) und ab t_light bzw. am Ende
 * kult-video--light (Abdunklung und Vignette blenden im CSS aus). Zeiten aus data-times „t_b,t_c,t_d,t_e,t_light“,
 * aufsteigend erzwungen; t_light ist unabhängig (darf vor t_e liegen).
 * Endscreen (kult-video--static; das CSS zeigt Endbild + Szene E, Download wird abgebrochen), ausgelöst durch:
 * error an <video>/<source> (vor diesem Skript vom Inline-Skript als data-video-error vermerkt) · play() abgelehnt
 * (NotAllowedError auch nach stummem Zweitversuch, oder Quelle unbrauchbar) · 6 s nach erstem Sichtkontakt noch kein
 * „playing“ · waiting/stalled länger als 4 s ohne Fortschritt · readyState < 2 noch 8 s nach dem Start des Skripts
 * (wenn die Section bis dahin gesehen wurde) · vorzeitiges „ended“, das ein einmaliges Neuladen nicht behebt ·
 * prefers-reduced-motion · kein IntersectionObserver. Die Zeit-Wächter ruhen in einem verborgenen Tab (der Browser
 * startet Medien dort nicht) und laufen beim Sichtbarwerden mit voller Frist neu. Nichts wird gespeichert: beim
 * nächsten Laden gilt wieder der normale Ablauf.
 * Theme-Editor: shopify:section:load (neu) / unload (destroy); Reduced-Motion-Wechsel: teardown + init.
 */
(function () {
  'use strict';
  if (window.kultHomeVideo) return;

  var SCENES = 'abcde';
  var TIMES = [3.9, 5.2, 7.8, 9.5, 9.5]; // Fallback t_b, t_c, t_d, t_e, t_light in Sekunden (Defaults der Section)
  var DURATION = 12.9; // Fallback-Dauer, bis die Metadaten geladen sind
  var SHOW = 0.5; // Sichtbarkeit, ab der das Video startet bzw. weiterläuft
  var START_MS = 9000; // so lange darf der Start nach dem ersten Sichtkontakt dauern (iPhones laden erst beim Startversuch, 18.10.2026)
  var NACHFRIST_MS = 9000; // einmalige Verlängerung der Start-/Ready-Frist, solange der Download erkennbar läuft (Mobilnetz)
  var STALL_MS = 6000; // so lange darf das Video ohne Fortschritt hängen
  var READY_MS = 8000; // danach muss mindestens das aktuelle Bild geladen sein (readyState ≥ 2)
  var STEPS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
  var MEDIA_EVENTS = ['loadedmetadata', 'durationchange', 'play', 'playing', 'pause', 'waiting', 'stalled', 'timeupdate', 'ended'];
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var items = [];

  /* Diagnose-Overlay (nur mit ?video-debug=1 in der Adresse, 18.10.2026): zeigt am Gerät, WARUM das Video
     nicht läuft — Bewegung-reduzieren, abgelehntes play() (Name des Fehlers), Wächter-Gründe, Ladezustand. */
  var DEBUG = /video-debug/.test(window.location.search);
  var debugBox = null;
  function debugZeile(text) {
    if (!DEBUG) return;
    try {
      if (!debugBox) {
        debugBox = document.createElement('div');
        debugBox.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:99999;background:rgba(20,20,18,.88);color:#9be89b;font:11px/1.5 Menlo,monospace;padding:8px 10px;max-height:45vh;overflow:auto;pointer-events:none;white-space:pre-wrap;';
        document.body.appendChild(debugBox);
      }
      var s = ((performance.now() / 1000).toFixed(1)) + 's  ' + text;
      debugBox.textContent += (debugBox.textContent ? '\n' : '') + s;
    } catch (e) { /* Diagnose darf nie stören */ }
  }

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  function clock(minutes) {
    var total = Math.floor(minutes * 60 + 0.0001), s = total % 60;
    return Math.floor(total / 60) + ':' + (s < 10 ? '0' : '') + s;
  }

  function isMedia(node) { return !!node && (node.nodeName === 'VIDEO' || node.nodeName === 'SOURCE'); }

  // data-times = "t_b,t_c,t_d,t_e,t_light" (Sekunden); ungültige Werte → Default, t_b–t_e aufsteigend erzwungen, t_light frei.
  function readTimes(el) {
    var raw = (el.getAttribute('data-times') || '').split(',');
    var out = [], prev = 0;
    for (var i = 0; i < TIMES.length; i++) {
      var t = parseFloat(raw[i]);
      if (!isFinite(t) || t < 0) t = TIMES[i];
      if (i < 4) prev = out[i] = Math.max(t, prev); // t_b … t_e aufsteigend
      else out[i] = t; // t_light darf vor t_e liegen (Aufhellen, sobald die Tüte im Bild ist)
    }
    return out;
  }

  // Endscreen: das CSS zeigt Endbild + Szene E; kein Abspielen, Download abgebrochen, kein „Nochmal ansehen“.
  function setStatic(el) {
    debugZeile('Endscreen wird gezeigt (setStatic). reduce=' + reduce.matches);
    var v = el.querySelector('[data-video]'), replay = el.querySelector('[data-video-replay]');
    el.classList.remove('kult-video--ready', 'kult-video--ended', 'kult-video--light');
    el.classList.add('kult-video--static');
    el.setAttribute('data-scene', 'e');
    if (replay) replay.hidden = true;
    if (!v || v.hasAttribute('data-video-still')) return;
    v.setAttribute('data-video-still', '');
    try { v.pause(); v.preload = 'none'; v.load(); } catch (e) {}
  }

  function Player(el) {
    var self = this, timer = el.querySelector('[data-video-timer]');
    this.el = el;
    this.stage = el.querySelector('[data-video-stage]') || el;
    this.video = el.querySelector('[data-video]');
    this.timer = timer;
    this.replayBtn = el.querySelector('[data-video-replay]');
    this.minutes = (timer && parseFloat(timer.getAttribute('data-minutes'))) || 20;
    this.unit = (timer && timer.getAttribute('data-unit')) || '';
    this.times = readTimes(el);
    this.dur = DURATION;
    this.scene = this.text = this.p = this.b = '';
    this.light = null;
    this.plays = 0; // Anzahl „playing“-Ereignisse (0 = nie gestartet)
    this.stallAt = 0;
    this.timers = {};
    this.ended = this.failed = this.dead = this.recovered = this.retried = this.wanted = this.seenOnce = this.startNachfrist = false;
    this.raf = 0;
    this.io = this.onceMeta = null;
    this.tick = function () { self.raf = 0; self.sync(); self.loop(); };

    el.setAttribute('data-video-init', '');
    el.classList.remove('kult-video--static', 'kult-video--ended', 'kult-video--light');
    el.classList.add('kult-video--ready');
    if (this.video) debugZeile('Init: reduce=' + reduce.matches + ' readyState=' + this.video.readyState + ' networkState=' + this.video.networkState + ' preload=' + this.video.preload + ' muted=' + this.video.muted + ' quelle=' + String(this.video.currentSrc || this.video.querySelector('source') && this.video.querySelector('source').src || '').slice(-40));
    if (!this.prepare()) return;
    this.render(0, false);
    if (this.replayBtn) this.replayBtn.addEventListener('click', this);
    document.addEventListener('visibilitychange', this);
    // Lädt das Video noch (networkState LOADING), verlängert sich die Frist bis zu zweimal statt hart zu enden (Mobilnetz)
    this.readyExtra = 0;
    this.arm('ready', READY_MS, function readyFrist() {
      if (!self.seenOnce || self.plays || self.video.readyState >= 2) return;
      if (self.video.networkState === 2 && self.readyExtra < 2) { self.readyExtra++; self.arm('ready', READY_MS, readyFrist); return; }
      self.fail('Lade-Frist abgelaufen (noch kein Bild geladen)');
    });
    this.io = new IntersectionObserver(function (entries) { self.view(entries[entries.length - 1]); }, { threshold: STEPS });
    this.io.observe(el);
  }

  // Video vorbereiten: stumm und inline auch als Property (iOS), Ereignisse, Fehlerstatus. false = Endscreen.
  Player.prototype.prepare = function () {
    var self = this, v = this.video;
    if (!v) { this.fail('kein <video> gefunden'); return false; }
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.preload = 'auto';
    if (v.hasAttribute('data-video-still')) { // war Endscreen (z. B. Reduced Motion): wieder vollständig laden
      v.removeAttribute('data-video-still');
      try { v.load(); } catch (e) {}
    }
    /* Markup lädt mit preload="none" (LCP-Fix 16.10.2026: Poster zuerst); hier startet das Laden fürs Autoplay,
       nachdem Poster und kritisches CSS ihre Priorität gesetzt haben (Skript lädt defer). */
    if (v.readyState === 0 && !v.error) { try { v.load(); } catch (e) {} }
    MEDIA_EVENTS.forEach(function (type) { v.addEventListener(type, self); });
    // error bubbelt nicht (an <source> schon gar nicht bis zum <video>): Capture auf der Section fängt beide.
    this.el.addEventListener('error', this, true);
    if (v.error || this.el.hasAttribute('data-video-error')) { this.fail('Medienfehler beim Laden der Seite' + (v.error ? ' (Code ' + v.error.code + ')' : '')); return false; }
    if (v.readyState >= 1) this.meta();
    return true;
  };

  Player.prototype.handleEvent = function (e) {
    var v = this.video;
    if (this.dead) return;
    if (DEBUG && e.type !== 'timeupdate') debugZeile('Ereignis: ' + e.type + ' (readyState=' + v.readyState + ', networkState=' + v.networkState + ')');
    switch (e.type) {
      case 'loadedmetadata':
      case 'durationchange': this.meta(); break;
      case 'play': this.loop(); break;
      case 'playing': this.plays++; this.disarm('stall'); this.loop(); break;
      case 'pause': this.halt(); this.sync(); break;
      case 'waiting':
      case 'stalled': this.watchStall(); break;
      case 'timeupdate':
        if (this.timers.stall && v.currentTime - this.stallAt > 0.1) this.disarm('stall');
        this.sync();
        break;
      case 'ended':
        // Vorzeitiges „ended“ weit vor der Dauer (abgebrochener Download, z. B. Dev-Proxy ohne Range-Support,
        // schwankendes Mobilnetz): einmal neu laden und weiterlaufen; passiert es erneut → Endscreen.
        if (this.dur && v.currentTime < this.dur - 0.5) {
          if (this.recovered) this.fail('vorzeitiges Ende auch nach Neuladen');
          else this.recover();
          break;
        }
        this.finish();
        break;
      case 'error': if (isMedia(e.target)) this.fail('Medienfehler-Ereignis' + (v.error ? ' (Code ' + v.error.code + ')' : '')); break;
      case 'click': if (e.currentTarget === this.replayBtn) this.replay(); break;
      case 'visibilitychange': if (!document.hidden) this.rearm(); break;
    }
  };

  // Wächter: benannte Timeouts, die beim Endscreen/Abbau alle gelöscht werden. In einem verborgenen Tab hält der
  // Browser Medien an; dort lösen sie nicht aus, sondern starten beim Sichtbarwerden mit voller Frist neu (rearm).
  Player.prototype.arm = function (name, ms, fn) {
    var self = this, w = { ms: ms, fn: fn, id: 0 };
    this.disarm(name);
    this.timers[name] = w;
    w.id = setTimeout(function () {
      w.id = 0;
      if (self.dead || self.timers[name] !== w || document.hidden) return;
      self.timers[name] = null;
      fn();
    }, ms);
  };

  Player.prototype.disarm = function (name) {
    var w = this.timers[name];
    if (w && w.id) clearTimeout(w.id);
    this.timers[name] = null;
  };

  Player.prototype.rearm = function () {
    var self = this;
    Object.keys(this.timers).forEach(function (name) {
      var w = self.timers[name];
      if (w) self.arm(name, w.ms, w.fn);
    });
  };

  // waiting/stalled, während das Video laufen soll: kein Fortschritt binnen 4 s → Endscreen.
  Player.prototype.watchStall = function () {
    var self = this, v = this.video;
    if (!this.wanted || this.ended || this.timers.stall) return;
    this.stallAt = v.currentTime;
    this.arm('stall', STALL_MS, function () {
      if (self.wanted && !self.ended && v.currentTime - self.stallAt < 0.05) self.fail('haengt ohne Fortschritt (waiting/stalled)');
    });
  };

  Player.prototype.recover = function () {
    var self = this, v = this.video, t = v.currentTime || 0, plays = this.plays;
    this.recovered = true;
    this.onceMeta = function () {
      v.removeEventListener('loadedmetadata', self.onceMeta);
      self.onceMeta = null;
      try { v.currentTime = t; } catch (err) {}
      self.play();
    };
    v.addEventListener('loadedmetadata', this.onceMeta);
    try { v.load(); } catch (err) { this.fail('Neuladen fehlgeschlagen'); return; }
    this.arm('recover', START_MS, function () { if (self.plays === plays) self.fail('Neuladen ohne Start'); });
  };

  Player.prototype.meta = function () {
    var d = this.video.duration;
    if (isFinite(d) && d > 0) this.dur = d;
    if (this.ended) this.render(this.dur, true);
    else this.sync();
  };

  // Ab ≥ 50 % sichtbar: starten bzw. weiterlaufen (beim ersten Mal mit 6-s-Startwächter); ganz aus dem Viewport: pausieren.
  Player.prototype.view = function (e) {
    var self = this, v = this.video, vh = (e.rootBounds && e.rootBounds.height) || window.innerHeight || 0;
    var seen = e.isIntersecting && (e.intersectionRatio >= SHOW - 0.01 || (vh > 0 && e.intersectionRect.height >= vh * SHOW));
    if (this.dead || this.ended) return;
    if (seen) {
      if (!this.seenOnce) {
        this.seenOnce = true;
        this.arm('start', START_MS, function startFrist() {
          if (self.plays) return;
          // Download läuft oder Metadaten sind da → einmal Nachfrist statt Endscreen (iPhone im Mobilnetz)
          if (!self.startNachfrist && (self.video.networkState === 2 || self.video.readyState >= 1)) { self.startNachfrist = true; self.arm('start', NACHFRIST_MS, startFrist); return; }
          self.fail('Start-Frist abgelaufen (kein playing)');
        });
      }
      if (v.paused) this.play();
    } else if (!e.isIntersecting && !v.paused) {
      this.wanted = false;
      this.disarm('stall');
      v.pause();
    }
  };

  Player.prototype.play = function () {
    var self = this, v = this.video, r;
    if (this.dead || this.ended) return;
    this.wanted = true;
    var no = function (err) {
      var name = err && err.name;
      debugZeile('play() abgelehnt: ' + name + ' — ' + String(err && err.message).slice(0, 80));
      if (self.dead || self.ended || name === 'AbortError') return; // pause() kam dazwischen
      if (name === 'NotAllowedError' && !self.retried) { self.retried = true; v.muted = true; self.play(); return; } // stummer Zweitversuch
      self.fail('play() abgelehnt (' + name + ')'); // abgelehnt oder Quelle unbrauchbar: Endscreen
    };
    try { r = v.play(); } catch (err) { no(err); return; }
    if (r && typeof r.then === 'function') r.then(null, no);
  };

  // Zustand zu einer Videozeit: Szene, --p, --b, Aufhellen (ab t_light), Timer (end = letztes Bild, Szene E, Endwerte).
  Player.prototype.render = function (t, end) {
    var tm = this.times, el = this.el, i = 0;
    var p = end ? 1 : clamp01(t / (this.dur || DURATION));
    var b = end ? 1 : clamp01((t - tm[0]) / Math.max(tm[2] - tm[0], 0.001));
    var light = end || t >= tm[4];
    if (end) i = 4;
    else while (i < 4 && t >= tm[i]) i++;
    var scene = SCENES.charAt(i), ps = p.toFixed(4), bs = b.toFixed(4);
    if (ps !== this.p) { this.p = ps; el.style.setProperty('--p', ps); }
    if (bs !== this.b) { this.b = bs; el.style.setProperty('--b', bs); }
    if (scene !== this.scene) { this.scene = scene; el.setAttribute('data-scene', scene); }
    if (light !== this.light) { this.light = light; el.classList.toggle('kult-video--light', light); }
    if (this.timer) {
      var text = clock(b * this.minutes) + (this.unit ? ' ' + this.unit : '');
      if (text !== this.text) { this.text = text; this.timer.textContent = text; }
    }
  };

  Player.prototype.sync = function () {
    if (!this.dead && !this.ended) this.render(this.video.currentTime || 0, false);
  };

  Player.prototype.loop = function () {
    var v = this.video;
    if (!this.raf && !this.dead && !this.ended && !v.paused && !v.ended) this.raf = requestAnimationFrame(this.tick);
  };

  Player.prototype.halt = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  // Ende: letztes Bild bleibt, Szene E mit Endwerten, aufgehellt; „Nochmal ansehen“ erscheint.
  Player.prototype.finish = function () {
    if (this.ended || this.dead) return;
    this.ended = true;
    this.wanted = false;
    this.halt();
    this.disarm('stall');
    this.disarm('recover');
    this.render(this.dur, true);
    this.el.classList.add('kult-video--ended');
    if (this.replayBtn) this.replayBtn.hidden = false;
  };

  Player.prototype.replay = function () {
    var focused = document.activeElement === this.replayBtn;
    if (this.dead) return;
    this.ended = false;
    this.el.classList.remove('kult-video--ended');
    if (this.replayBtn) this.replayBtn.hidden = true;
    if (focused) { try { this.stage.focus({ preventScroll: true }); } catch (e) { this.stage.focus(); } }
    try { this.video.currentTime = 0; } catch (e) {}
    this.render(0, false);
    this.play();
  };

  Player.prototype.fail = function (grund) {
    if (this.failed || this.dead) return;
    debugZeile('AUFGEGEBEN: ' + (grund || 'ohne Grund gemeldet'));
    this.failed = true;
    this.destroy();
    setStatic(this.el);
  };

  Player.prototype.destroy = function () {
    if (this.dead) return;
    var self = this, v = this.video, el = this.el;
    this.dead = true;
    this.halt();
    Object.keys(this.timers).forEach(function (name) { self.disarm(name); });
    if (this.io) this.io.disconnect();
    document.removeEventListener('visibilitychange', this);
    el.removeEventListener('error', this, true);
    if (v) {
      MEDIA_EVENTS.forEach(function (type) { v.removeEventListener(type, self); });
      if (this.onceMeta) v.removeEventListener('loadedmetadata', this.onceMeta);
      try { v.pause(); } catch (e) {}
    }
    if (this.replayBtn) {
      this.replayBtn.removeEventListener('click', this);
      this.replayBtn.hidden = true;
    }
    el.classList.remove('kult-video--ready', 'kult-video--ended', 'kult-video--light');
    el.removeAttribute('data-video-init');
    el.style.removeProperty('--p');
    el.style.removeProperty('--b');
  };

  function sections(root) {
    if (!root || !root.querySelectorAll) return [];
    var list = Array.prototype.slice.call(root.querySelectorAll('[data-video-autoplay]'));
    if (root.matches && root.matches('[data-video-autoplay]')) list.unshift(root);
    return list;
  }

  function init(root) {
    sections(root).forEach(function (el) {
      if (el.hasAttribute('data-video-init')) return;
      if (reduce.matches || !('IntersectionObserver' in window)) setStatic(el);
      else items.push(new Player(el));
    });
  }

  function teardown(root) {
    items = items.filter(function (item) {
      if (root && !root.contains(item.el)) return true;
      item.destroy();
      return false;
    });
  }

  function onReduceChange() {
    teardown(null);
    init(document);
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { teardown(event.target); });
  if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);
  else if (reduce.addListener) reduce.addListener(onReduceChange);

  window.kultHomeVideo = { init: init, teardown: teardown };
})();
